import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Copy, Users, Trophy, Share2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useUser } from "@/contexts/UserContext";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ReferralPage() {
  const { user } = useUser();
  const [referralData, setReferralData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Referral Program | MarkeeMix - Earn Free Pro";
    if (user) {
      fetchReferralCode();
    }
    fetchLeaderboard();
  }, [user]);

  const fetchReferralCode = async () => {
    try {
      const res = await fetch(`${API}/api/referral/my-code?user_id=${user.user_id}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setReferralData(data);
      }
    } catch (e) {
      console.error("Failed to fetch referral code", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/api/referral/leaderboard`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (e) {
      console.error("Failed to fetch leaderboard", e);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const shareReferral = () => {
    if (navigator.share && referralData) {
      navigator.share({
        title: "Join MarkeeMix - Get Free Marketing Tools",
        text: "I've been using MarkeeMix for SEO and marketing. Join using my link and we both get rewards!",
        url: referralData.referral_link
      });
    } else if (referralData) {
      copyToClipboard(referralData.referral_link);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
            <Gift className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Invite Friends, Earn Free Pro
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Share MarkeeMix with friends. Get 1 month of Pro free for every friend who upgrades!
          </p>
        </div>

        {/* How it Works */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {[
            { icon: <Share2 />, title: "Share your link", desc: "Send your unique referral link to friends" },
            { icon: <Users />, title: "Friend signs up", desc: "They create an account using your link" },
            { icon: <Gift />, title: "Both get rewarded", desc: "You get 1 month Pro free when they upgrade" }
          ].map((step, i) => (
            <Card key={i} className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                {step.icon}
              </div>
              <h3 className="font-bold mb-1">{step.title}</h3>
              <p className="text-sm text-gray-500">{step.desc}</p>
            </Card>
          ))}
        </div>

        {/* Referral Link Card */}
        {user ? (
          <Card className="p-6 mb-10">
            <h2 className="text-xl font-bold mb-4">Your Referral Link</h2>
            {loading ? (
              <p className="text-gray-500">Loading...</p>
            ) : referralData ? (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input 
                    value={referralData.referral_link} 
                    readOnly 
                    className="flex-1 bg-gray-50"
                  />
                  <Button onClick={() => copyToClipboard(referralData.referral_link)}>
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <Button variant="outline" onClick={shareReferral}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Your Code:</span>
                    <span className="font-mono font-bold text-purple-600">{referralData.code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span><strong>{referralData.total_referrals}</strong> invited</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span><strong>{referralData.successful_referrals}</strong> upgraded</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-500" />
                    <span><strong>{referralData.rewards_earned}</strong> months earned</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-red-500">Failed to load referral code</p>
            )}
          </Card>
        ) : (
          <Card className="p-6 mb-10 text-center">
            <h2 className="text-xl font-bold mb-2">Sign in to get your referral link</h2>
            <p className="text-gray-500 mb-4">Create an account or sign in to start earning rewards</p>
            <Button asChild>
              <a href="/login">Sign In</a>
            </Button>
          </Card>
        )}

        {/* Leaderboard */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h2 className="text-xl font-bold">Top Referrers</h2>
          </div>
          {leaderboard.length > 0 ? (
            <div className="space-y-3">
              {leaderboard.map((item, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${i === 0 ? 'bg-yellow-50' : i === 1 ? 'bg-gray-100' : i === 2 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-orange-800' : 'bg-gray-200 text-gray-600'}`}>
                      {item.rank}
                    </span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <span className="font-bold text-purple-600">{item.referrals} referrals</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Be the first to refer friends!</p>
          )}
        </Card>
      </main>

      <Footer />
    </div>
  );
}
